import type { CSSProperties } from 'react';
import type { BallColor } from '@aiscoreboard/contracts';
import type { MessageKey } from './i18n';

/**
 * What each ball looks like.
 *
 * Shared by the scoreboard's ball pad and the phone remote so the two can
 * never disagree about which ball is which. They are laid out differently —
 * one is read across a room, the other is held — but a brown button that is
 * brown in one place and green in the other is a scoring mistake waiting to
 * happen.
 */
export const BALL_SWATCH: Record<BallColor, string> = {
  red: 'bg-ball-red',
  yellow: 'bg-ball-yellow text-black',
  green: 'bg-ball-green',
  brown: 'bg-ball-brown',
  blue: 'bg-ball-blue',
  pink: 'bg-ball-pink text-black',
  black: 'bg-ball-black',
};

/**
 * The two balls whose paint is lighter than the ink normally written on it.
 * Kept here rather than in either pad, because a yellow that takes black
 * numerals on the scoreboard and white ones on the phone is the same ball
 * rendered two ways on two screens showing one frame.
 */
export const PALE_BALLS = new Set<BallColor>(['yellow', 'pink']);

/**
 * What turns a flat disc into a ball: a broad sheen up and left with a hot
 * mirror of the lamp burning through it, light bouncing back off the cloth
 * into the lower right, and the colour turning over only at the very edge.
 *
 * It is applied over `BALL_SWATCH` rather than replacing it, so there is still
 * exactly one place that decides what colour a pink is. Everything is sized in
 * `em`, which means the same constant works on a 112px pot button and on a
 * 22px chip in the break history without either looking like a scaled photo of
 * the other — the highlight stays where the light is, not where the pixels are.
 *
 * The highlight is three gradients rather than one. A single hard dot reads as
 * a sticker of a ball; a single soft patch reads as pale paint. Polished
 * phenolic resin does both at once — a wide diffuse sheen with a small mirror
 * of the lamp burning through the middle of it, and a fainter second lamp
 * beside it — and it is that layering, not the brightness of any one of them,
 * that makes a surface look hard and wet.
 *
 * The shading is deliberately shallow. An earlier version rolled the colour
 * off to near-black over most of the sphere, which is what light does to a
 * matte ball; on a glossy one the body colour stays bright almost to the
 * silhouette and only the last few percent turns, which is why these read as
 * lit balls and the darker version read as dull ones.
 *
 * The rim is an inset shadow rather than an `outline`, because a hairline
 * outline on a 22px circle is a ring of grey stair-steps; an inset shadow is
 * resolved by the same rasteriser that draws the gradient and stays smooth at
 * any size.
 */
export const BALL_GLOSS: CSSProperties = {
  backgroundImage: [
    // The mirror of the lamp: small, white-hot, hard-edged.
    'radial-gradient(circle at 35% 24%, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 3.4%, rgba(255,255,255,0.5) 6.5%, rgba(255,255,255,0) 11%)',
    // The sheen around it: wide, soft, offset the same way, which is what
    // stops the hot core reading as a speck of dust on the screen.
    'radial-gradient(ellipse 34% 30% at 34% 26%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.34) 42%, rgba(255,255,255,0.08) 74%, rgba(255,255,255,0) 100%)',
    // A second, fainter lamp: rooms have more than one, and one reflection
    // alone is the look of a rendered sphere rather than a photographed one.
    'radial-gradient(ellipse 18% 12% at 62% 20%, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)',
    // Bounce light off the table, opposite the specular. Without it a ball
    // reads as a dome; with it, as a sphere sitting on something.
    'radial-gradient(circle at 66% 80%, rgba(255,255,255,0.34) 0%, rgba(255,255,255,0.12) 18%, rgba(255,255,255,0) 40%)',
    // Body colour lifted a little where it faces the light, so the middle of
    // the ball is brighter than the paint rather than exactly the paint.
    'radial-gradient(circle at 40% 34%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 62%)',
    // The turn, kept to the outer fifth: bright ball, dark silhouette.
    'radial-gradient(circle at 42% 38%, rgba(0,0,0,0) 62%, rgba(0,0,0,0.12) 78%, rgba(0,0,0,0.42) 92%, rgba(0,0,0,0.62) 100%)',
  ].join(','),
  boxShadow: [
    'inset 0 0.05em 0.1em rgba(255,255,255,0.55)',
    'inset 0 -0.12em 0.22em rgba(0,0,0,0.3)',
    'inset 0 0 0 0.03em rgba(0,0,0,0.35)',
    // The ball sits on the bar rather than floating over it: a tight contact
    // shadow directly under it, and a wider soft one further down.
    '0 0.06em 0.09em rgba(0,0,0,0.5)',
    '0 0.15em 0.28em rgba(0,0,0,0.4)',
  ].join(','),
};

/**
 * The message key naming a ball, including the `color` the state uses when any
 * colour is on.
 *
 * A ball's name reaches the screen in three different sentences — the button's
 * tooltip, "On: …", and an AI suggestion — and translating it at each of those
 * call sites is how one of them ends up still in English.
 */
export function ballKey(color: BallColor | 'color'): MessageKey {
  return `ball.${color}` as MessageKey;
}
